import React, { useContext, useEffect, useState } from 'react';
import { Divider, Fab, ListItemButton, Icon, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button } from '@engyalo/design-system';
import { RoutingMethods } from 'types/user';
import { SESSION_TYPE } from 'types/chat';
import { TAB_MENU } from 'types/room';
import useMobile from 'hooks/useMobile';
import NoConversations from 'components/NoConversations';
import BottomNavigation from 'components/BottomNavigation';
import { LoadingWrapper } from 'views/home/Settings/style';
import { flag, useFeatureFlag } from 'hooks/useFeatureFlag';
import { ReactComponent as Loading } from '../../../../assets/icons/loadingYalo.svg';
import { SessionInfoView } from '../../../../components/sessions/SessionInfoView';
import { CollapseView } from '../../../../components/sessions/CollapseView';
import { Container, IconButton, ButtonsStack } from '../../../../components/sessions/style';
import SearchAndStatusBar from '../../../../components/SearchAndStatusBar';
import MenuChatsContext from '../../../../contexts/menuChats/context';
import { ISessionProps } from '../../../../contexts/menuChats/types';
import ChatItemFilter from '../../../../components/ChatItemFilter';
import ChatsContext from '../../../../contexts/chats/context';
import shadows from '../../../../theme/shadows';
import WebsocketContext from '../../../../contexts/websocket/context';
import SnackbarCustom from '../../../../components/Snackbar';
import AuthContext from '../../../../contexts/auth/context';
import useSegment from '../../../../services/Analytics';
import ChatTabsMenu from '../ChatTabsMenu';
import { ChatList } from '../style';
import { UnReadBadge } from './style';

const Sessions: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { sendEvent, EVENT_NAMES } = useSegment();
  const {
    accordeonsYourChats,
    handleExpand,
    isSelectedItems,
    handleSelectedItems,
    handleSearchChats,
    searchYourChats,
    isSearchYourChats,
    refreshTemplates,
    isLoading,
  } = useContext(MenuChatsContext);
  const {
    handleShowNotificationMarkedInDiscussion,
    showNotificationMarkedInDiscussion,
    discussionNotificationContent,
  } = useContext(WebsocketContext);
  const { goToNewChat, handleOpenDiscussion } = useContext(ChatsContext);
  const { allowsManualOnHold, routingMethod } = useContext(AuthContext);
  const { showMobile } = useMobile();
  const isStartedByYouEnabled = useFeatureFlag(flag.STARTED_BY_YOU);

  const [openSnackbarSuccess, setOpenSnackbarSuccess] = useState(false);
  const [preValue, setPreValue] = useState('');

  const showYourChats = !isSearchYourChats && !!accordeonsYourChats?.length;
  const showPendingChats = routingMethod === RoutingMethods.ManualSelection;

  const chats = accordeonsYourChats?.filter((item) => {
    const shouldRemovePending = item.id === SESSION_TYPE.PENDING && !showPendingChats;
    const shouldRemoveOnHold = item.id === SESSION_TYPE.ON_HOLD && !allowsManualOnHold;
    return !shouldRemovePending && !shouldRemoveOnHold;
  });

  const handleClickSettings = () => {
    navigate('/mobile-settings');
  };

  const handleChatClick = () => {
    navigate('/');
  };

  const handleSnackbar = () => {
    setOpenSnackbarSuccess(false);
  };

  useEffect(() => {
    if (showNotificationMarkedInDiscussion) setOpenSnackbarSuccess(true);
  }, [showNotificationMarkedInDiscussion]);

  return (
    <Container>
      <SearchAndStatusBar
        value={preValue}
        handleChangeSearch={(value: string) => {
          if (value !== 'delete') {
            handleSearchChats('yourChats', value);
            setPreValue(value);
          } else {
            const newValue = preValue.substring(0, preValue.length - 1);

            handleSearchChats('yourChats', newValue);
            setPreValue(newValue);
          }
        }}
      />

      <ChatList disablePadding>
        {isSearchYourChats &&
          searchYourChats &&
          searchYourChats.length > 1 &&
          searchYourChats.map((item) => (
            <React.Fragment key={item._id}>
              <ChatItemFilter
                tabType="yourChats"
                id={item._id}
                name={item.fname}
                closedAt={item.closedAt}
                handleOpenChat={() => goToNewChat(item)}
                department={item.department}
                closedBy={item.closedBy}
                servedBy={item.servedBy}
              />
              <Divider variant="fullWidth" component="li" />
            </React.Fragment>
          ))}

        {isSearchYourChats && searchYourChats && searchYourChats.length === 0 ? (
          <NoConversations title="notFoundChats" />
        ) : null}
        {isLoading && (
          <LoadingWrapper data-testid="loadingWrapper">
            <Loading />
          </LoadingWrapper>
        )}
        {showMobile && showYourChats ? (
          <ChatTabsMenu chats={chats} type={TAB_MENU.YOUR_CHATS} isLoading={isLoading} />
        ) : null}

        {!showMobile && showYourChats
          ? chats.map((item: ISessionProps) => {
              if (item.id === SESSION_TYPE.STARTED_BY_YOU && !isStartedByYouEnabled) {
                return null;
              }

              return (
                <React.Fragment key={item.id}>
                  <div>
                    <ListItemButton
                      disableGutters
                      onClick={() => {
                        sendEvent(EVENT_NAMES.CONVERSATION_SELECTED, {
                          queue: item.id,
                        });
                        if (
                          item.id === SESSION_TYPE.IN_ATTENDANCE ||
                          item.id === SESSION_TYPE.PENDING
                        ) {
                          return;
                        }
                        handleExpand(item, 'accordeonsYourChats');
                      }}
                      style={{
                        position: 'sticky',
                        zIndex: 99,
                      }}
                      sx={{
                        ':hover': {
                          background: 'neutral.var10 !important',
                        },
                      }}
                    >
                      <SessionInfoView type={TAB_MENU.YOUR_CHATS} item={item} refreshAction={refreshTemplates} />
                    </ListItemButton>
                  </div>
                  <Divider variant="fullWidth" component="li" />
                  {item.id === SESSION_TYPE.STARTED_BY_YOU && item.templates?.length && isStartedByYouEnabled ? (
                    <CollapseView type={TAB_MENU.YOUR_CHATS} item={item} />
                  ) : null}
                  {item.rooms.length ? <CollapseView type={TAB_MENU.YOUR_CHATS} item={item} /> : null}
                </React.Fragment>
              );
            })
          : null}

        {showNotificationMarkedInDiscussion ? (
          <>
            <Fab
              data-testid="btnOpenDiscussion"
              onClick={() => {
                if (discussionNotificationContent) {
                  navigate(`/${discussionNotificationContent.prid}`);
                }

                handleOpenDiscussion(true);
                handleShowNotificationMarkedInDiscussion(false);
              }}
              size="small"
              sx={{
                alignSelf: 'end',
                position: 'sticky',
                bottom: '16px',
                left: '88%',
                backgroundColor: 'common.white',
                boxShadow: shadows.shadowBlack4,
              }}
            >
              <Icon
                className="fa-solid fa-screen-users"
                sx={{
                  color: 'neutral.var70',
                }}
              />

              <UnReadBadge>1</UnReadBadge>
            </Fab>

            <SnackbarCustom
              data-testid="snackbarCustom"
              open={openSnackbarSuccess}
              message={
                `${discussionNotificationContent?.lastMessage?.u?.name}
          ${t('markedYouInInternalMessage')}
          ${discussionNotificationContent?.lastMessage?.msg}` ?? ''
              }
              handleClose={handleSnackbar}
              type="info"
              discussion={discussionNotificationContent}
            />
          </>
        ) : null}

        {showMobile && (
          <Stack position="fixed" bottom="1.8rem" left="1.2rem" right="1.2rem" zIndex={1}>
            <BottomNavigation
              conversationAction={handleChatClick}
              settingsAction={handleClickSettings}
              currentActiveIndex={0}
            />
          </Stack>
        )}
      </ChatList>
      {isSelectedItems ? (
        <ButtonsStack onClick={() => handleSelectedItems()} data-testid="btnSelectedItems">
          <IconButton>
            <Icon
              className="fa-regular fa-xmark fa-xs"
              sx={{
                color: 'neutral.var80',
              }}
            />
          </IconButton>
          <Button>{t('finalize')} </Button>
        </ButtonsStack>
      ) : null}
    </Container>
  );
};

export default Sessions;
